// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the colours a viewer chose for each dataset, kept in this
 * browser (localStorage `ayni-display-prefs.v1`) and, signed in, on the
 * account (`displayPrefsSync.ts`). See `displayPrefsMerge.ts` for what an
 * entry holds and docs/AYNI_XR_PALETTE_GROUPS_PLAN.md for the rules.
 *
 * A dataset opens with, highest first: the viewer's saved colours, its
 * display group's (`displayGroups.ts`), then its own published ones.
 * Only fields the viewer changed are saved, so the rest keep following
 * the default.
 */

import type { Dataset } from '../types'
import { DEFAULT_DISPLAY, type ColorScaleDisplay, type DisplayLimits } from './colorScaleDisplay'
import { groupDisplay } from './displayGroups'
import { sanitizeDisplayPrefsDoc, type DisplayPref, type DisplayPrefsDoc } from './displayPrefsMerge'

export const DISPLAY_PREFS_STORAGE_KEY = 'ayni-display-prefs.v1'

let cache: DisplayPrefsDoc | null = null
const listeners = new Set<() => void>()

function load(): DisplayPrefsDoc {
  if (cache) return cache
  let raw: unknown = null
  try {
    raw = JSON.parse(window.localStorage.getItem(DISPLAY_PREFS_STORAGE_KEY) ?? 'null')
  } catch {
    // Blocked or unreadable storage: colours simply are not remembered.
  }
  cache = sanitizeDisplayPrefsDoc(raw) ?? { prefs: {} }
  return cache
}

function store(doc: DisplayPrefsDoc, notify: boolean): void {
  cache = doc
  try {
    window.localStorage.setItem(DISPLAY_PREFS_STORAGE_KEY, JSON.stringify(doc))
  } catch {
    // Kept for this page only.
  }
  if (notify) for (const l of listeners) l()
}

/** The default a dataset opens with: its display group's colours, else its own. */
export function defaultDisplayOf(dataset: Pick<Dataset, 'displayGroup'> | null | undefined): ColorScaleDisplay {
  return dataset?.displayGroup ? groupDisplay(dataset.displayGroup) : DEFAULT_DISPLAY
}

/** The viewer's saved colours for a dataset over its default; the default when none. */
export function resolveDisplay(defaults: ColorScaleDisplay, pref: DisplayPref | undefined): ColorScaleDisplay {
  if (!pref || pref.reset) return defaults
  return {
    ...defaults,
    ...(pref.palette !== undefined ? { palette: pref.palette } : {}),
    ...(pref.inverted !== undefined ? { inverted: pref.inverted } : {}),
    ...(pref.limits ? { limits: { ...pref.limits }, stretch: { lo: 0, hi: 1 } } : {}),
  }
}

/** A dataset's saved entry, if the viewer changed its colours. */
export function displayPrefOf(datasetId: string): DisplayPref | undefined {
  const pref = load().prefs[datasetId]
  return pref && !pref.reset ? pref : undefined
}

/** The colours a dataset opens with now. */
export function displayFor(dataset: Pick<Dataset, 'id' | 'displayGroup'>): ColorScaleDisplay {
  return resolveDisplay(defaultDisplayOf(dataset), displayPrefOf(dataset.id))
}

function sameLimits(a: DisplayLimits | null | undefined, b: DisplayLimits | null | undefined): boolean {
  if (!a || !b) return !a && !b
  const close = (x: number, y: number) => Math.abs(x - y) <= 1e-9 * Math.max(1, Math.abs(x), Math.abs(y))
  return a.scale === b.scale && close(a.min, b.min) && close(a.max, b.max)
}

/** What of `display` differs from `defaults`, as an entry; null when nothing does. */
export function prefFromDisplay(display: ColorScaleDisplay, defaults: ColorScaleDisplay, nowMs: number): DisplayPref | null {
  const pref: DisplayPref = { updatedAt: new Date(nowMs).toISOString() }
  if (display.palette !== defaults.palette) pref.palette = display.palette
  if (!!display.inverted !== !!defaults.inverted) pref.inverted = !!display.inverted
  if (display.limits && !sameLimits(display.limits, defaults.limits)) pref.limits = { ...display.limits }
  return pref.palette !== undefined || pref.inverted !== undefined || pref.limits ? pref : null
}

/**
 * Remember the viewer's colours for a dataset. When they match its
 * default again, the entry becomes a reset, so other devices drop theirs.
 */
export function saveDisplayFor(datasetId: string, display: ColorScaleDisplay, defaults: ColorScaleDisplay, nowMs = Date.now()): void {
  const doc = load()
  const pref = prefFromDisplay(display, defaults, nowMs)
  const had = doc.prefs[datasetId]
  if (!pref && (!had || had.reset)) return
  const next = pref ?? { reset: true as const, updatedAt: new Date(nowMs).toISOString() }
  if (had && !had.reset && pref && JSON.stringify({ ...had, updatedAt: '' }) === JSON.stringify({ ...pref, updatedAt: '' })) return
  store({ prefs: { ...doc.prefs, [datasetId]: next } }, true)
}

/** Forget the viewer's colours for a dataset: it opens with its default again. */
export function resetDisplayFor(datasetId: string, nowMs = Date.now()): void {
  const had = load().prefs[datasetId]
  if (!had || had.reset) return
  store({ prefs: { ...load().prefs, [datasetId]: { reset: true, updatedAt: new Date(nowMs).toISOString() } } }, true)
}

/** The whole set, for the account copy. */
export function exportDisplayPrefsDoc(): DisplayPrefsDoc {
  return load()
}

/** Replace the set with the account's merge; listeners are not told (the sync caused it). */
export function applyDisplayPrefsDoc(doc: DisplayPrefsDoc): void {
  store(doc, false)
  for (const l of appliedListeners) l()
}

const appliedListeners = new Set<() => void>()

/** Called after the viewer changes colours here. Returns an unsubscribe. */
export function onDisplayPrefsChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Called after a merge from the account replaced the set (to redraw the globe). Returns an unsubscribe. */
export function onDisplayPrefsApplied(listener: () => void): () => void {
  appliedListeners.add(listener)
  return () => appliedListeners.delete(listener)
}

/** Tests only: read storage afresh. */
export function resetDisplayPrefsForTests(): void {
  cache = null
}
